import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/AuthContext";
import { adminStats, adminList } from "../lib/api";
import { LogOut, Users, Mail, MessageCircle, Activity, ShoppingBag, BookOpen, MessageSquare } from "lucide-react";

const TABS = [
  { key: "overview", label: "Übersicht" },
  { key: "newsletter", label: "Newsletter" },
  { key: "contacts", label: "Kontakte" },
  { key: "applications", label: "Bewerbungen" },
  { key: "quiz", label: "Quiz" },
  { key: "wheel", label: "Lebensrad" },
  { key: "users", label: "Users" },
  { key: "payments", label: "Payments" },
  { key: "commissions", label: "Provisionen" },
];

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="glass rounded-2xl p-6">
    <div className="flex items-center justify-between mb-3">
      <span className="text-xs uppercase tracking-widest text-white/45">{label}</span>
      <Icon size={16} className="text-[#D4AF37]" />
    </div>
    <div className="serif text-4xl font-light">{value}</div>
  </div>
);

const AdminDashboard = () => {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("overview");
  const [stats, setStats] = useState(null);
  const [rows, setRows] = useState([]);
  const [loadingRows, setLoadingRows] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) { nav("/admin/login"); return; }
    const adminRoles = new Set(["admin", "SUPER_ADMIN", "ACADEMY_ADMIN", "CONTENT_EDITOR",
                                "MENTOR_REVIEWER", "FINANCE_MANAGER", "SUPPORT_AGENT", "FACILITATOR"]);
    if (!adminRoles.has(user.role)) { nav("/"); return; }
    adminStats().then(setStats);
  }, [user, loading, nav]);

  useEffect(() => {
    if (tab === "overview" || !user) return;
    setLoadingRows(true);
    adminList(tab).then((r) => setRows(r)).finally(() => setLoadingRows(false));
  }, [tab, user]);

  if (loading || !user) return <div className="pt-32 text-center text-white/60">Lade…</div>;

  const cols = {
    newsletter: ["email", "first_name", "source", "language", "created_at"],
    contacts: ["first_name", "last_name", "email", "topic", "message", "created_at"],
    quiz: ["email", "first_name", "weakest_rule", "language", "created_at"],
    wheel: ["email", "first_name", "average", "weakest", "created_at"],
    users: ["email", "name", "role", "referral_code", "created_at"],
    payments: ["product_key", "amount", "currency", "payment_status", "user_email", "session_id", "created_at"],
    commissions: ["created_at", "referrer_email", "buyer_email", "product_key", "order_amount", "amount", "status"],
    applications: ["created_at", "program_key", "first_name", "email", "phone", "readiness_1to10", "status"],
  }[tab] || [];

  return (
    <div className="pt-24 min-h-screen" data-testid="admin-dashboard">
      <div className="container-sat py-10">
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="overline">SAT · Admin</div>
            <h1 className="headline-xl text-4xl mt-2">Willkommen, {user.name}</h1>
          </div>
          <div className="flex gap-2">
            <Link to="/admin/courses" className="btn-secondary text-sm" data-testid="admin-courses-link">Course Editor</Link>
            <Link to="/admin/v2/courses" className="btn-primary text-sm" data-testid="admin-v2-courses-link">V2 Kurs-Engine</Link>
            <Link to="/admin/drips" className="btn-secondary text-sm" data-testid="admin-drips-link">Drip-Kampagnen</Link>
            <Link to="/admin/broadcast" className="btn-secondary text-sm" data-testid="admin-broadcast-link">Broadcast</Link>
            <Link to="/admin/testimonials" className="btn-secondary text-sm" data-testid="admin-testimonials-link">Testimonials</Link>
            <Link to="/admin/events" className="btn-secondary text-sm" data-testid="admin-events-link">Events</Link>
            <Link to="/admin/checkout-recovery" className="btn-secondary text-sm" data-testid="admin-recovery-link">Recovery</Link>
            <Link to="/admin/voice-inquiries" className="btn-secondary text-sm" data-testid="admin-voices-link">Stimmen-Anfragen</Link>
            <Link to="/admin/satbot" className="btn-secondary text-sm" data-testid="admin-satbot-link">SATbot</Link>
            <Link to="/admin/settings" className="btn-secondary text-sm" data-testid="admin-settings-link">Settings</Link>
            <Link to="/admin/herzbotschafter" className="btn-secondary text-sm" data-testid="admin-herzbotschafter-link">Herzbotschafter</Link>
            <button onClick={() => { logout(); nav("/"); }} className="btn-secondary text-sm" data-testid="admin-logout">
              <LogOut size={14} /> Abmelden
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex flex-wrap gap-2 mb-8" data-testid="admin-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-full border text-sm transition ${tab === t.key ? "bg-[#D4AF37] text-[#081328] border-[#D4AF37]" : "border-white/10 text-white/65 hover:bg-white/[0.04]"}`}
              data-testid={`admin-tab-${t.key}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4" data-testid="admin-stats-grid">
            <StatCard icon={Mail} label="Newsletter" value={stats.newsletter} />
            <StatCard icon={MessageCircle} label="Kontakte" value={stats.contacts} />
            <StatCard icon={Activity} label="Quiz" value={stats.quiz_results} />
            <StatCard icon={Activity} label="Lebensrad" value={stats.wheel_results} />
            <StatCard icon={Users} label="Users" value={stats.users} />
            <StatCard icon={BookOpen} label="Kurse" value={stats.courses} />
            <StatCard icon={BookOpen} label="Enrollments" value={stats.enrollments} />
            <StatCard icon={ShoppingBag} label="Paid Orders" value={stats.payments_paid} />
            <StatCard icon={ShoppingBag} label="Umsatz CHF" value={(stats.revenue_chf || 0).toFixed(2)} />
            <StatCard icon={MessageSquare} label="Forum Threads" value={stats.forum_threads} />
          </div>
        )}

        {tab !== "overview" && (
          <div className="glass rounded-2xl overflow-hidden" data-testid={`admin-table-${tab}`}>
            {loadingRows ? <div className="p-8 text-white/55 text-sm">Lade…</div> : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-white/[0.03] text-xs uppercase tracking-widest text-white/45">
                    <tr>{cols.map((c) => <th key={c} className="text-left px-5 py-4">{c}</th>)}</tr>
                  </thead>
                  <tbody>
                    {rows.length === 0 && <tr><td colSpan={cols.length} className="px-5 py-8 text-center text-white/40">Keine Einträge</td></tr>}
                    {rows.map((r, i) => (
                      <tr key={r.id || i} className="border-t border-white/5 hover:bg-white/[0.02]">
                        {cols.map((c) => (
                          <td key={c} className="px-5 py-3 text-white/80 max-w-[280px] truncate" title={String(r[c] ?? "")}>{String(r[c] ?? "—").slice(0, 80)}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        <div className="mt-10 text-xs text-white/35">
          <Link to="/" className="hover:text-white">← Zurück zur Website</Link>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
